import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  Chip,
  Stack,
  Checkbox,
} from "@mui/material";
import {
  CloudUpload,
  Edit,
  Save,
  Cancel,
  Warning,
  Download,
  Folder,
} from "@mui/icons-material";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { GridActionsCellItem } from "@mui/x-data-grid-pro";
import { styled } from "@mui/material/styles";
import Papa from "papaparse";
import { capitalize } from "../../actprep-ui/utils/formatting";
import { apiUrls } from "../../actprep-ui/utils/urls";
import { useGet, useGeneralAPI } from "../../actprep-ui/hooks/api";
import { DataGridPro } from "@mui/x-data-grid-pro";
import ModelAutoComplete from "../../actprep-ui/components/ModelAutoComplete";
import VideoForm from "../VideoForm";
import {
  answerChoiceOptions,
  testingVariants,
} from "../../actprep-ui/constants/testing";
import api from "../../actprep-ui/utils/api";
import {
  showSuccessSnackbar,
  showErrorSnackbar,
} from "../../actprep-ui/utils/feedback";
import {
  EXPECTED_CSV_HEADERS,
  buildImageMap,
  chunkFilesByQuestion,
  countMissingImages,
  defaultChoicesFromAnswer,
  formatChoices,
  imageUploadHadProblems,
  isImageFile,
  isZipFile,
  listZipFilenames,
  mapCsvRow,
  matchImageFiles,
  normalizeFilename,
  parseChoices,
  parseFieldTest,
  validateImportQuestion,
} from "./testAnswersImportUtils";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

function ImageMatchCell({ filename, matched }) {
  if (!filename) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }
  return (
    <Stack spacing={0.5} sx={{ py: 0.5 }}>
      <Typography variant="body2" noWrap title={filename}>
        {filename}
      </Typography>
      <Chip
        size="small"
        label={matched ? "Matched" : "Missing"}
        color={matched ? "success" : "warning"}
        variant="outlined"
      />
    </Stack>
  );
}

export default function TestAnswersImportDialog({
  open,
  onClose,
  test,
  subject,
  expectedQuestionCount,
}) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState(null);
  const [testQuestions, setTestQuestions] = useState([]);
  const [editingRowId, setEditingRowId] = useState(null);
  const [editingRowData, setEditingRowData] = useState(null);
  const [imageFiles, setImageFiles] = useState([]);
  const [zipFile, setZipFile] = useState(null);
  const [zipFilenames, setZipFilenames] = useState([]);
  const [unmatchedFiles, setUnmatchedFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const zipInputRef = useRef(null);

  const categorySubject = useMemo(() => {
    if (test?.variant === testingVariants.LESSON) {
      return test.lesson?.subject || null;
    }
    return subject;
  }, [test, subject]);

  const { data: categoryData } = useGet(apiUrls.admin.categories(), {
    params: {
      search_params: {
        subject: categorySubject,
      },
      size: 500,
    },
    enabled: !!categorySubject,
  });
  const { data: videoData } = useGeneralAPI("Video", {
    size: 0,
    values: ["uuid", "title"],
    customSerializer: true,
  });

  const categories = useMemo(
    () => categoryData?.data || [],
    [categoryData?.data]
  );
  const videos = useMemo(() => videoData?.data || [], [videoData?.data]);

  const categoryMap = useMemo(() => {
    const map = new Map();
    categories.forEach((cat) => {
      map.set(cat.name.toLowerCase(), cat);
    });
    return map;
  }, [categories]);

  const videoMap = useMemo(() => {
    const map = new Map();
    videos.forEach((video) => {
      if (video.title) {
        map.set(String(video.title).toLowerCase(), video);
      }
    });
    return map;
  }, [videos]);

  const applyLookups = useCallback(
    (rows) =>
      rows.map((row) => {
        const categoryName = row.category || "";
        const videoTitle = row.video || "";
        const categoryMatch = categoryName
          ? categoryMap.get(categoryName.toLowerCase())
          : null;
        const videoMatch = videoTitle
          ? videoMap.get(videoTitle.toLowerCase())
          : null;
        return {
          ...row,
          matchedCategory:
            row.matchedCategory ||
            (categoryMatch
              ? { label: categoryMatch.name, uuid: categoryMatch.uuid }
              : null),
          matchedVideo:
            row.matchedVideo ||
            (videoMatch
              ? { label: videoMatch.title, uuid: videoMatch.uuid }
              : null),
        };
      }),
    [categoryMap, videoMap]
  );

  const filesByName = useMemo(() => {
    const map = new Map();
    zipFilenames.forEach((name) => {
      const key = normalizeFilename(name);
      if (key) map.set(key, true);
    });
    imageFiles.forEach((file) => {
      const key = normalizeFilename(file.name);
      if (key) map.set(key, file);
    });
    return map;
  }, [imageFiles, zipFilenames]);

  const testQuestionsRef = useRef(testQuestions);
  testQuestionsRef.current = testQuestions;

  useEffect(() => {
    const rows = testQuestionsRef.current;
    if (!rows.length) return;
    const { matchedQuestions, unmatchedFiles: leftover } = matchImageFiles(
      rows,
      filesByName
    );
    setTestQuestions(applyLookups(matchedQuestions));
    setUnmatchedFiles(leftover);
  }, [filesByName, applyLookups]);

  const validateQuestion = useCallback(
    (question, allQuestions = []) =>
      validateImportQuestion(question, allQuestions),
    []
  );

  const validationErrors = useMemo(() => {
    const errors = {};
    testQuestions.forEach((question) => {
      const questionErrors = validateQuestion(question, testQuestions);
      if (Object.keys(questionErrors).length > 0) {
        errors[question.id] = questionErrors;
      }
    });
    return errors;
  }, [testQuestions, validateQuestion]);

  const totalErrorCount = useMemo(() => {
    return Object.values(validationErrors).reduce(
      (sum, fieldErrors) =>
        sum +
        Object.values(fieldErrors).reduce(
          (fieldSum, messages) => fieldSum + messages.length,
          0
        ),
      0
    );
  }, [validationErrors]);

  const questionCountMismatch = useMemo(() => {
    return (
      testQuestions.length > 0 &&
      expectedQuestionCount != null &&
      testQuestions.length !== expectedQuestionCount
    );
  }, [testQuestions.length, expectedQuestionCount]);

  const unmatchedCategoryCount = useMemo(
    () =>
      testQuestions.filter((row) => row.category && !row.matchedCategory)
        .length,
    [testQuestions]
  );
  const unmatchedVideoCount = useMemo(
    () => testQuestions.filter((row) => row.video && !row.matchedVideo).length,
    [testQuestions]
  );
  const missingImageCount = useMemo(
    () => countMissingImages(testQuestions),
    [testQuestions]
  );

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith(".csv") && file.type !== "text/csv") {
      setError("Please select a CSV file.");
      return;
    }

    setSelectedFile(file);
    setError(null);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const parsed = results.data
          .map((raw, index) => {
            const row = mapCsvRow(raw);
            const answer = String(row.correct_answer || "").toUpperCase();
            const parsedChoices = parseChoices(row.answer_choices);
            const defaultChoices = defaultChoicesFromAnswer(answer);
            const choices = parsedChoices.length
              ? parsedChoices
              : defaultChoices || [];
            return {
              id: index,
              question_number: row.question_number || "",
              correct_answer: answer,
              answer_choices: choices,
              choices_were_blank: parsedChoices.length === 0,
              category: row.category || "",
              originalCategory: row.category || "",
              video: row.video || "",
              originalVideo: row.video || "",
              primary_image: row.primary_image || "",
              supplemental_image_1: row.supplemental_image_1 || "",
              supplemental_image_2: row.supplemental_image_2 || "",
              primary_image_matched: false,
              supplemental_image_1_matched: false,
              supplemental_image_2_matched: false,
              is_field_test_question: parseFieldTest(
                row.is_field_test_question
              ),
              matchedCategory: null,
              matchedVideo: null,
            };
          })
          .filter((row) => row.question_number || row.correct_answer);
        setTestQuestions(applyLookups(parsed));
      },
      error: (parseError) => {
        console.error("CSV parsing error:", parseError);
        setError("Failed to parse CSV file. Please check the file format.");
      },
    });
  };

  const absorbImageFiles = useCallback(async (fileList) => {
    const incoming = Array.from(fileList || []);
    const nextImages = [];
    let nextZip = null;
    for (const file of incoming) {
      if (isZipFile(file)) {
        nextZip = file;
      } else if (isImageFile(file)) {
        nextImages.push(file);
      }
    }
    if (nextImages.length) {
      setImageFiles((prev) => {
        const byName = new Map(prev.map((file) => [normalizeFilename(file.name), file]));
        nextImages.forEach((file) => {
          byName.set(normalizeFilename(file.name), file);
        });
        return Array.from(byName.values());
      });
    }
    if (nextZip) {
      setZipFile(nextZip);
      try {
        setZipFilenames(await listZipFilenames(nextZip));
      } catch (zipError) {
        console.error(zipError);
        setZipFilenames([]);
      }
    }
  }, []);

  const handleEditRow = useCallback((row) => {
    setEditingRowId(row.id);
    setEditingRowData({
      ...row,
      answer_choices_text: formatChoices(row.answer_choices),
    });
  }, []);

  const handleSaveRow = useCallback(() => {
    if (editingRowId == null || !editingRowData) return;

    const categoryName = editingRowData.category || "";
    const matchedCategory =
      editingRowData.matchedCategory ||
      (categoryName
        ? (() => {
            const match = categoryMap.get(categoryName.toLowerCase());
            return match ? { label: match.name, uuid: match.uuid } : null;
          })()
        : null);
    const videoTitle = editingRowData.video || "";
    const matchedVideo =
      editingRowData.matchedVideo ||
      (videoTitle
        ? (() => {
            const match = videoMap.get(videoTitle.toLowerCase());
            return match ? { label: match.title, uuid: match.uuid } : null;
          })()
        : null);
    const parsedChoices = parseChoices(
      editingRowData.answer_choices_text ?? editingRowData.answer_choices
    );

    setTestQuestions((prev) =>
      prev.map((row) =>
        row.id === editingRowId
          ? {
              ...editingRowData,
              answer_choices: parsedChoices,
              choices_were_blank: parsedChoices.length === 0,
              matchedCategory,
              matchedVideo,
            }
          : row
      )
    );

    setEditingRowId(null);
    setEditingRowData(null);
  }, [editingRowId, editingRowData, categoryMap, videoMap]);

  const handleCancelEdit = useCallback(() => {
    setEditingRowId(null);
    setEditingRowData(null);
  }, []);

  const handleEditingFieldChange = useCallback((field, value) => {
    setEditingRowData((prev) => {
      if (!prev) return prev;
      const next = { ...prev, [field]: value };
      if (field === "correct_answer" && prev.choices_were_blank) {
        next.answer_choices = defaultChoicesFromAnswer(value) || [];
        next.answer_choices_text = formatChoices(next.answer_choices);
      }
      return next;
    });
  }, []);

  const handleCategoryChange = useCallback((newCategory) => {
    setEditingRowData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        category: newCategory ? newCategory.label : "",
        matchedCategory: newCategory,
      };
    });
  }, []);

  const handleVideoChange = useCallback((newVideo) => {
    setEditingRowData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        video: newVideo ? newVideo.label : "",
        matchedVideo: newVideo,
      };
    });
  }, []);

  const resetState = useCallback(() => {
    setSelectedFile(null);
    setError(null);
    setTestQuestions([]);
    setEditingRowId(null);
    setEditingRowData(null);
    setImageFiles([]);
    setZipFile(null);
    setZipFilenames([]);
    setUnmatchedFiles([]);
    setSubmitting(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (imageInputRef.current) imageInputRef.current.value = "";
    if (folderInputRef.current) folderInputRef.current.value = "";
    if (zipInputRef.current) zipInputRef.current.value = "";
  }, []);

  const handleClose = () => {
    resetState();
    onClose();
  };

  const uploadImageBatches = async (imageMap) => {
    const results = {
      attached: [],
      unmatched_filenames: [],
      missing_questions: [],
      incomplete_questions: [],
    };
    const fileChunks = chunkFilesByQuestion(imageMap, imageFiles);
    const chunks = fileChunks.length ? fileChunks : [[]];

    for (const chunk of chunks) {
      if (!zipFile && !chunk.length) continue;
      const form = new FormData();
      form.append("test_id", test.uuid);
      form.append("subject", subject);
      form.append("image_map", JSON.stringify(imageMap));
      if (zipFile) form.append("zip_file", zipFile);
      chunk.forEach((file) => form.append("image_files", file));
      const response = await api.post(
        apiUrls.admin.batchTestQuestionImages(),
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      if (!response.data?.success) {
        throw new Error(response.data?.reason || "Failed to attach images");
      }
      const data = response.data.data || {};
      results.attached.push(...(data.attached || []));
      (data.unmatched_filenames || []).forEach((name) => {
        if (!results.unmatched_filenames.includes(name)) {
          results.unmatched_filenames.push(name);
        }
      });
      (data.missing_questions || []).forEach((number) => {
        if (!results.missing_questions.includes(number)) {
          results.missing_questions.push(number);
        }
      });
      (data.incomplete_questions || []).forEach((number) => {
        if (!results.incomplete_questions.includes(number)) {
          results.incomplete_questions.push(number);
        }
      });
    }
    return results;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const cleanTestQuestions = testQuestions.map((testQuestion) => ({
        question_number: testQuestion.question_number,
        correct_answer: testQuestion.correct_answer,
        answer_choices: testQuestion.answer_choices,
        category_id: testQuestion.matchedCategory?.uuid ?? null,
        video_id: testQuestion.matchedVideo?.uuid ?? null,
        is_field_test_question: Boolean(testQuestion.is_field_test_question),
      }));
      const response = await api.post(apiUrls.admin.batchTestQuestions(), {
        subject,
        test_id: test.uuid,
        test_questions: cleanTestQuestions,
      });
      if (!response.data?.success) {
        showErrorSnackbar(response.data?.reason || "Failed to import answers");
        return;
      }

      const imageMap = buildImageMap(testQuestions);
      if (imageMap.length && (imageFiles.length || zipFile)) {
        try {
          const imageResults = await uploadImageBatches(imageMap);
          if (imageUploadHadProblems(imageResults, imageMap)) {
            showErrorSnackbar(
              "Answers saved, but some images could not be attached."
            );
            handleClose();
            return;
          }
        } catch (imageError) {
          showErrorSnackbar(
            imageError.message ||
              "Answers saved, but some images could not be attached."
          );
          handleClose();
          return;
        }
      }

      showSuccessSnackbar("Answers imported successfully!");
      handleClose();
    } catch {
      // Axios interceptor already surfaces request failures.
    } finally {
      setSubmitting(false);
    }
  };

  const autocompleteSlotProps = {
    popper: {
      sx: (theme) => ({
        zIndex: theme.zIndex.modal + 10,
      }),
    },
  };

  const columns = useMemo(
    () => [
      {
        field: "question_number",
        headerName: "Question #",
        width: 110,
        renderCell: (params) => {
          const isEditing = editingRowId === params.row.id;
          const fieldErrors =
            validationErrors[params.row.id]?.question_number || [];
          const hasError = fieldErrors.length > 0;
          if (isEditing) {
            return (
              <TextField
                type="number"
                value={editingRowData?.question_number || ""}
                onChange={(e) =>
                  handleEditingFieldChange("question_number", e.target.value)
                }
                size="small"
                error={hasError}
                helperText={fieldErrors[0]}
                sx={{ width: "100%" }}
              />
            );
          }
          return (
            <Typography
              sx={{
                color: hasError ? "error.main" : "inherit",
                fontWeight: hasError ? "bold" : "normal",
              }}
            >
              {params.value}
            </Typography>
          );
        },
      },
      {
        field: "correct_answer",
        headerName: "Answer",
        width: 100,
        renderCell: (params) => {
          const isEditing = editingRowId === params.row.id;
          const fieldErrors =
            validationErrors[params.row.id]?.correct_answer || [];
          const hasError = fieldErrors.length > 0;
          if (isEditing) {
            return (
              <FormControl fullWidth size="small" error={hasError}>
                <Select
                  value={editingRowData?.correct_answer || ""}
                  onChange={(e) =>
                    handleEditingFieldChange("correct_answer", e.target.value)
                  }
                >
                  {answerChoiceOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            );
          }
          return (
            <Typography
              sx={{
                color: hasError ? "error.main" : "inherit",
                fontWeight: hasError ? "bold" : "normal",
              }}
            >
              {params.value}
            </Typography>
          );
        },
      },
      {
        field: "answer_choices",
        headerName: "Choices",
        width: 150,
        renderCell: (params) => {
          const isEditing = editingRowId === params.row.id;
          const fieldErrors =
            validationErrors[params.row.id]?.answer_choices || [];
          if (isEditing) {
            return (
              <TextField
                size="small"
                value={editingRowData?.answer_choices_text || ""}
                onChange={(e) =>
                  handleEditingFieldChange("answer_choices_text", e.target.value)
                }
                error={fieldErrors.length > 0}
                helperText={fieldErrors[0]}
                sx={{ width: "100%" }}
              />
            );
          }
          return (
            <Typography
              color={fieldErrors.length ? "error.main" : "inherit"}
              variant="body2"
            >
              {formatChoices(params.value)}
            </Typography>
          );
        },
      },
      {
        field: "category",
        headerName: "Category",
        width: 220,
        renderCell: (params) => {
          const isEditing = editingRowId === params.row.id;
          const unmatched = Boolean(
            params.row.category && !params.row.matchedCategory
          );
          if (isEditing) {
            return (
              <ModelAutoComplete
                modelName="Category"
                label=""
                value={editingRowData?.matchedCategory || null}
                setValue={handleCategoryChange}
                defaultParams={{ subject: categorySubject }}
                getOptionLabel={(row) =>
                  row.label.replace(
                    `${capitalize(categorySubject || subject)}: `,
                    ""
                  )
                }
                slotProps={autocompleteSlotProps}
              />
            );
          }
          return (
            <Stack spacing={0.5}>
              <Typography variant="body2">
                {params.value || <em>No category</em>}
              </Typography>
              {unmatched && (
                <Chip
                  size="small"
                  color="warning"
                  icon={<Warning />}
                  label="Unmatched"
                  variant="outlined"
                />
              )}
            </Stack>
          );
        },
      },
      {
        field: "video",
        headerName: "Video",
        width: 220,
        renderCell: (params) => {
          const isEditing = editingRowId === params.row.id;
          const unmatched = Boolean(params.row.video && !params.row.matchedVideo);
          if (isEditing) {
            return (
              <ModelAutoComplete
                modelName="Video"
                label=""
                value={editingRowData?.matchedVideo || null}
                setValue={handleVideoChange}
                allowCreation
                CreationForm={(props) => (
                  <VideoForm
                    {...props}
                    DialogProps={{
                      slotProps: {
                        root: {
                          sx: (theme) => ({
                            zIndex: theme.zIndex.modal + 12,
                          }),
                        },
                      },
                    }}
                  />
                )}
                slotProps={autocompleteSlotProps}
              />
            );
          }
          return (
            <Stack spacing={0.5}>
              <Typography variant="body2">
                {params.value || <em>No video</em>}
              </Typography>
              {unmatched && (
                <Chip
                  size="small"
                  color="warning"
                  icon={<Warning />}
                  label="Unmatched"
                  variant="outlined"
                />
              )}
            </Stack>
          );
        },
      },
      {
        field: "is_field_test_question",
        headerName: "Field Test",
        width: 110,
        renderCell: (params) => {
          const isEditing = editingRowId === params.row.id;
          if (isEditing) {
            return (
              <Checkbox
                checked={Boolean(editingRowData?.is_field_test_question)}
                onChange={(e) =>
                  handleEditingFieldChange(
                    "is_field_test_question",
                    e.target.checked
                  )
                }
              />
            );
          }
          return params.value ? "Yes" : "";
        },
      },
      {
        field: "primary_image",
        headerName: "Primary - Question Image",
        width: 200,
        renderCell: (params) => (
          <ImageMatchCell
            filename={params.value}
            matched={params.row.primary_image_matched}
          />
        ),
      },
      {
        field: "supplemental_image_1",
        headerName: "Supplemental 1",
        width: 170,
        renderCell: (params) => (
          <ImageMatchCell
            filename={params.value}
            matched={params.row.supplemental_image_1_matched}
          />
        ),
      },
      {
        field: "supplemental_image_2",
        headerName: "Supplemental 2",
        width: 170,
        renderCell: (params) => (
          <ImageMatchCell
            filename={params.value}
            matched={params.row.supplemental_image_2_matched}
          />
        ),
      },
      {
        field: "actions",
        type: "actions",
        headerName: "Actions",
        width: 100,
        getActions: (params) => {
          const isEditing = editingRowId === params.row.id;
          if (isEditing) {
            return [
              <GridActionsCellItem
                key="save"
                icon={<Save />}
                label="Save"
                onClick={handleSaveRow}
                color="primary"
              />,
              <GridActionsCellItem
                key="cancel"
                icon={<Cancel />}
                label="Cancel"
                onClick={handleCancelEdit}
                color="error"
              />,
            ];
          }
          return [
            <GridActionsCellItem
              key="edit"
              icon={<Edit />}
              label="Edit"
              onClick={() => handleEditRow(params.row)}
            />,
          ];
        },
      },
    ],
    [
      editingRowId,
      editingRowData,
      validationErrors,
      subject,
      categorySubject,
      handleEditingFieldChange,
      handleCategoryChange,
      handleVideoChange,
      handleSaveRow,
      handleCancelEdit,
      handleEditRow,
    ]
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <Typography>
            Batch Import Answers for{" "}
            <strong>
              {test.name} ({capitalize(subject)})
            </strong>
          </Typography>
          {testQuestions.length > 0 && (
            <Chip
              label={`${testQuestions.length} questions`}
              color="primary"
              size="small"
            />
          )}
          {totalErrorCount > 0 && (
            <Chip
              label={`${totalErrorCount} validation error${
                totalErrorCount > 1 ? "s" : ""
              }`}
              color="error"
              size="small"
            />
          )}
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {questionCountMismatch && (
            <Alert severity="warning" icon={<Warning />}>
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                Question count mismatch
              </Typography>
              <Typography variant="body2">
                Expected: <strong>{expectedQuestionCount}</strong> questions |
                Actual: <strong>{testQuestions.length}</strong> questions. You
                can still import.
              </Typography>
            </Alert>
          )}

          {unmatchedCategoryCount > 0 && (
            <Alert severity="warning">
              {unmatchedCategoryCount} categor
              {unmatchedCategoryCount === 1 ? "y" : "ies"} did not match. Pick
              one in the grid or leave blank to clear on save.
            </Alert>
          )}
          {unmatchedVideoCount > 0 && (
            <Alert severity="warning">
              {unmatchedVideoCount} video
              {unmatchedVideoCount === 1 ? "" : "s"} did not match. Pick or
              create one in the grid, or leave blank to clear on save.
            </Alert>
          )}
          {missingImageCount > 0 && (
            <Alert severity="warning">
              {missingImageCount} image filename
              {missingImageCount === 1 ? "" : "s"} in the CSV did not match an
              uploaded file. Answers can still be imported.
            </Alert>
          )}
          {unmatchedFiles.length > 0 && (
            <Alert severity="warning">
              Unmatched leftover files: {unmatchedFiles.join(", ")}
            </Alert>
          )}

          {totalErrorCount > 0 && (
            <Alert severity="warning">
              Fix {totalErrorCount} validation error
              {totalErrorCount > 1 ? "s" : ""} before importing. Duplicate
              numbers and invalid answers/choices still block save.
            </Alert>
          )}

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button
              component="label"
              variant="outlined"
              startIcon={<CloudUpload />}
              disabled={testQuestions.length > 0}
            >
              {selectedFile ? `Selected: ${selectedFile.name}` : "Upload CSV"}
              <VisuallyHiddenInput
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={handleFileSelect}
              />
            </Button>
            <Button
              variant="outlined"
              startIcon={<Download />}
              href="/templates/clause_mastery.csv"
              download="clause_mastery.csv"
            >
              Download template
            </Button>
          </Stack>

          <Typography variant="body2" color="text.secondary">
            Expected CSV format: {EXPECTED_CSV_HEADERS}
          </Typography>

          {testQuestions.length > 0 && (
            <Stack spacing={1}>
              <Typography variant="subtitle2">Question images</Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<CloudUpload />}
                >
                  Add images
                  <VisuallyHiddenInput
                    ref={imageInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(event) => absorbImageFiles(event.target.files)}
                  />
                </Button>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<Folder />}
                >
                  Add folder
                  <VisuallyHiddenInput
                    ref={folderInputRef}
                    type="file"
                    multiple
                    onChange={(event) => absorbImageFiles(event.target.files)}
                    {...{ webkitdirectory: "", directory: "" }}
                  />
                </Button>
                <Button component="label" variant="outlined">
                  Add zip
                  <VisuallyHiddenInput
                    ref={zipInputRef}
                    type="file"
                    accept=".zip,application/zip"
                    onChange={(event) => absorbImageFiles(event.target.files)}
                  />
                </Button>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {imageFiles.length} image file{imageFiles.length === 1 ? "" : "s"}
                {zipFile ? ` · zip: ${zipFile.name}` : ""}
              </Typography>
            </Stack>
          )}

          {testQuestions.length > 0 && (
            <Box sx={{ height: 600, width: "100%" }}>
              <DataGridPro
                rows={testQuestions}
                columns={columns}
                disableColumnSorting
                disableColumnMenu
                getRowHeight={() => "auto"}
                hideFooter={testQuestions.length <= 100}
                pageSizeOptions={[25, 50, 100]}
                initialState={{ pinnedColumns: { right: ["actions"] } }}
                sx={{
                  "& .MuiDataGrid-cell": {
                    py: 1,
                    alignItems: "flex-start",
                  },
                }}
              />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Close
        </Button>
        {testQuestions.length > 0 && (
          <Button
            variant="contained"
            disabled={totalErrorCount > 0 || submitting}
            onClick={handleSubmit}
          >
            Import {testQuestions.length} Question
            {testQuestions.length > 1 ? "s" : ""}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
